import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import Logo from '@/components/Logo';
import { formatDate } from '@/lib/formatting';

export default function JoinPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventName, setEventName] = useState<string | null>(null);
  const [merchant, setMerchant] = useState<string | null>(null);
  const [receiptDate, setReceiptDate] = useState<string | null>(null);

  useEffect(() => {
    async function checkEvent() {
      if (!code) return;

      // Check if already joined
      const storedParticipant = api.getStoredParticipant(code);
      if (storedParticipant) {
        navigate(`/${code}/event`, { replace: true });
        return;
      }

      try {
        const { event } = await api.getEvent(code);
        setEventName(event.name);
        setMerchant(event.merchant);
        setReceiptDate(event.receiptDate);
      } catch (e) {
        if (e instanceof ApiError) {
          setError(e.message);
        } else {
          setError('Failed to load event');
        }
      } finally {
        setLoading(false);
      }
    }

    checkEvent();
  }, [code, navigate]);

  async function handleJoin(e: React.FormEvent) {
    e.preventDefault();
    if (!code || !name.trim()) return;

    setJoining(true);
    setError(null);

    try {
      await api.joinEvent(code, name.trim());
      navigate(`/${code}/event`);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError('Failed to join event');
      }
    } finally {
      setJoining(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="mb-6">
            <Logo fontSize={48} />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error && !eventName) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="mb-4">
            <Logo fontSize={40} />
          </div>
          <div className="text-6xl mb-4">404</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h1>
          <p className="text-gray-600">This event doesn't exist or the code has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 pt-52">
      <div className="text-center max-w-md w-full">
        {/* Logo and event info */}
        <div>
          <Logo fontSize={48} />
          <p className="text-gray-900 font-medium mt-2">{eventName || '\u00A0'}</p>
          <p className="text-gray-500 text-sm">
            {merchant || receiptDate ? (
              <>
                {merchant}
                {merchant && receiptDate && ' · '}
                {receiptDate && formatDate(receiptDate)}
              </>
            ) : '\u00A0'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleJoin} className="mb-4 mt-8">
          <label className="block text-sm text-gray-500 mb-2">
            Enter your name to claim your items
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
              autoComplete="name"
            />
            <button
              type="submit"
              disabled={joining || !name.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {joining ? '...' : 'Continue'}
            </button>
          </div>
        </form>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Open in App link for iOS users */}
        {/iPhone|iPad|iPod/.test(navigator.userAgent) && code && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <a
              href={`trueup://${code}`}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Open in TrueUp App
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
